import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createAccount } from "../../services/Api";
import { PASSWORDLENGTH } from "./Login";
import { Eye, EyeOff } from "lucide-react";
import Tooltip from "../../components/Tooltip";

const CreateAccountPage = () => {
  //sets some functions and states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [birthdate, setBirthdate] = useState("1990-01-01");
  const [pin, setPin] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [primaryInterestRate, setPrimaryInterestRate] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);
  // const { checkAuth } = useAuth();

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleRepeatPasswordVisibility = () =>
    setShowRepeatPassword(!showRepeatPassword);
  const togglePinVisibility = () => setShowPin(!showPin);

  const [inputErrors, setInputErrors] = useState({
    inputName: "",
    inputEmail: "",
    inputPassword: "",
    inputRepeatPassword: "",
    inputBirthdate: "",
    inputPin: "",
    inputInterestRate: "",
  });

  // Validation function
  const validateCreateAccountForm = () => {
    let valid = true;
    const newErrors = {};

    if (!firstName.trim() || !lastName.trim()) {
      newErrors.inputName = "First and last name is required";
      valid = false;
    }
    // Validate username
    if (!email.trim()) {
      newErrors.inputEmail = "Email is required";
      valid = false;
    } else if (!email.includes("@") || !email.includes(".")) {
      newErrors.inputEmail = "Enter a valid email address";
      valid = false;
    }

    // Validate password (minimum 2 characters)
    if (!password || password.length < PASSWORDLENGTH) {
      newErrors.inputPassword =
        "Password must be at least " + PASSWORDLENGTH + " characters";
      valid = false;
    }

    if (repeatPassword !== password) {
      newErrors.inputRepeatPassword =
        "The passwords you have typed do not match";
      valid = false;
    }

    if (!birthdate) {
      newErrors.inputBirthdate = "Birthdate is required";
      valid = false;
    }

    if (!pin || pin.length < 4) {
      newErrors.inputPin = "Pin must be exactly 4 numbers";
      valid = false;
    }

    if (!primaryInterestRate) {
      newErrors.inputInterestRate = "Interest rate is required";
      valid = false;
    } else if (isNaN(primaryInterestRate) || primaryInterestRate < 0) {
      newErrors.inputInterestRate =
        "Interest rate must be a number greater than 0";
      valid = false;
    }

    setInputErrors(newErrors);
    return valid;
  };

  //function that is called when login button is submitted
  const handleAccountCreation = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!validateCreateAccountForm()) {
      setIsLoading(false);
      return;
    }

    try {
      //calls post function "login"
      const response = await createAccount(
        firstName,
        lastName,
        email,
        password,
        birthdate,
        pin,
        familyName
      );

      navigate("/add-child", {
        state: {
          guardianID: response.GuardianID,
          familyName: familyName,
          email: email,
          primaryRate: primaryInterestRate,
          doCreateToken: true,
        },
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="container">
        <div className="card o-hidden border-0 shadow-lg my-5">
          <div className="card-body p-0">
            {/* Nested Row within Card Body */}
            <div className="row">
              <div className="col-lg-2 d-none d-lg-block bg-register-image" />
              <div className="col-lg-8">
                <div className="p-5">
                  <div className="text-center">
                    <h1 className="h4 text-gray-900 mb-4">
                      Create an Account!
                    </h1>
                  </div>
                  <form className="user">
                    <div className="form-group text-left">
                      <label
                        htmlFor="firstNameInputCreateAccount"
                        className="d-block font-weight-bold mb-1 ms-2"
                      >
                        First Name:
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-user"
                        id="firstNameInputCreateAccount"
                        placeholder="First Name"
                        onChange={(e) => setFirstName(e.target.value)}
                        value={firstName || ""}
                      />
                    </div>
                    <div className="form-group text-left">
                      <label
                        htmlFor="lastNameInputCreateAccount"
                        className="d-block font-weight-bold mb-1 ms-2"
                      >
                        Last Name:
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-user"
                        id="lastNameInputCreateAccount"
                        placeholder="Last Name"
                        onChange={(e) => setLastName(e.target.value)}
                        value={lastName || ""}
                      />
                    </div>
                    {inputErrors.inputName && (
                      <p className="text-danger">{inputErrors.inputName}</p>
                    )}
                    <div className="form-group text-left">
                      <label
                        htmlFor="emailInputCreateAccount"
                        className="d-block font-weight-bold mb-1 ms-2"
                      >
                        Email Address:
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-user"
                        id="emailInputCreateAccount"
                        placeholder="Email Address"
                        onChange={(e) => setEmail(e.target.value)}
                        value={email || ""}
                      />
                    </div>
                    {inputErrors.inputEmail && (
                      <p className="text-danger">{inputErrors.inputEmail}</p>
                    )}
                    <div className="form-group text-left position-relative">
                      <label
                        htmlFor="passwordCreateAccount"
                        className="d-block font-weight-bold mb-1 ms-2"
                      >
                        Password:
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control form-control-user pe-5"
                          id="passwordCreateAccount"
                          placeholder="Password"
                          onChange={(e) => setPassword(e.target.value)}
                          value={password || ""}
                        />
                        <span
                          className="passwordToggle"
                          onClick={togglePasswordVisibility}
                          style={{ cursor: "pointer" }}
                        >
                          {showPassword ? <EyeOff /> : <Eye />}
                        </span>
                      </div>
                    </div>
                    <div className="form-group text-left position-relative">
                      <label
                        htmlFor="repeatPasswordCreateAccount"
                        className="d-block font-weight-bold mb-1 ms-2"
                      >
                        Repeat Password:
                      </label>
                      <div className="position-relative">
                        <input
                          type={showRepeatPassword ? "text" : "password"}
                          className="form-control form-control-user"
                          id="repeatPasswordCreateAccount"
                          placeholder="Repeat Password"
                          onChange={(e) => setRepeatPassword(e.target.value)}
                          value={repeatPassword || ""}
                        />
                        <span
                          className="passwordToggle"
                          onClick={toggleRepeatPasswordVisibility}
                          style={{ cursor: "pointer" }}
                        >
                          {showRepeatPassword ? <EyeOff /> : <Eye />}
                        </span>
                      </div>
                    </div>
                    {inputErrors.inputPassword && (
                      <p className="text-danger">{inputErrors.inputPassword}</p>
                    )}
                    {inputErrors.inputRepeatPassword && (
                      <p className="text-danger">
                        {inputErrors.inputRepeatPassword}
                      </p>
                    )}
                    <div className="form-group text-left">
                      <label
                        htmlFor="birthdateCreateAccount"
                        className="d-block font-weight-bold mb-1 ms-2"
                      >
                        Birthdate:
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-user"
                        id="birthdateCreateAccount"
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setBirthdate(e.target.value)}
                        value={birthdate || ""}
                      />
                    </div>
                    {inputErrors.inputBirthdate && (
                      <p className="text-danger">
                        {inputErrors.inputBirthdate}
                      </p>
                    )}
                    <div className="form-group text-left position-relative">
                      <label
                        htmlFor="pinInputCreateAccount"
                        className="d-block font-weight-bold mb-1 ms-2"
                      >
                        4-Digit PIN:
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPin ? "text" : "password"}
                          className="form-control form-control-user"
                          id="pinInputCreateAccount"
                          placeholder="4-Digit PIN"
                          maxLength="4"
                          onInput={(e) =>
                          (e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          ))
                          }
                          onChange={(e) => setPin(e.target.value)}
                          value={pin || ""}
                        />
                        <span
                          className="passwordToggle"
                          onClick={togglePinVisibility}
                          style={{ cursor: "pointer" }}
                        >
                          {showPin ? <EyeOff /> : <Eye />}
                        </span>
                      </div>
                    </div>
                    {inputErrors.inputPin && (
                      <p className="text-danger">{inputErrors.inputPin}</p>
                    )}
                    <div className="form-group text-left">
                      <label
                        htmlFor="familyNameInputCreateAccount"
                        className="d-block font-weight-bold mb-1 ms-2"
                      >
                        Family Name (Optional):
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-user"
                        id="familyNameInputCreateAccount"
                        placeholder="Family Name"
                        maxLength="32"
                        onChange={(e) => setFamilyName(e.target.value)}
                        value={familyName || ""}
                      />
                    </div>
                    <div className="form-group text-left">
                      <div className="d-inline-flex align-items-center">
                        <label
                          htmlFor="initialFamilyInterestInputCreateAccount"
                          className="font-weight-bold mb-1 ms-2"
                        >
                          Monthly Family Interest Rate:
                        </label>
                        <div className="mt-n1">
                          <Tooltip
                            content="?"
                            description="Encourage your child to save! At the end of each month, you—the parent—pay interest on their balance at the rate you set, teaching them the power of compound interest. For example, with a 10% family interest rate, a $100 balance would grow to $110. You can adjust this rate anytime in account settings."
                          />
                        </div>
                      </div>
                      <input
                        type="number"
                        className="form-control form-control-user"
                        id="initialFamilyInterestInputCreateAccount"
                        placeholder="Monthly Rate Percentage"
                        min="0"
                        onChange={(e) => setPrimaryInterestRate(e.target.value)}
                        onInput={(e) =>
                        (e.target.value = e.target.value.replace(
                          /[^0-9]/g,
                          ""
                        ))
                        }
                        value={primaryInterestRate || ""}
                      />
                    </div>
                    {inputErrors.inputInterestRate && (
                      <p className="text-danger">
                        {inputErrors.inputInterestRate}
                      </p>
                    )}
                    {error && <p className="text-danger">{error}</p>}
                    <br />
                    <button
                      onClick={handleAccountCreation}
                      style={{ width: "80%" }}
                      className={`confirm-btn p-3 ${isLoading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      disabled={isLoading}
                    >
                      {isLoading ? "Creating Account..." : "Next Step"}
                    </button>
                  </form>

                  <hr />
                  <div className="edit-btn grey-color center mb-2" style={{ width: "40%" }}>
                    <Link to="/login" className="small white-font-color" style={{ textDecoration: "none", color: "" }}>
                      Already have an account? Login!
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateAccountPage;
